import { Injectable } from '@angular/core';

/**
 * The visitor's altuid: one random identifier, created on first need and stored
 * on the device.
 *
 * There used to be three ways to produce one. Two are commented out below rather
 * than deleted, because the identifiers they minted are still in visitors'
 * localStorage and still key live rows: understanding a `5f3a2b1c9d8e` or a
 * `t1734829` found in the wild means being able to read the code that made it.
 * Nothing regenerates those values, so nothing needed migrating when the paths
 * were retired: fetchAndStoreUserIdentifierSync reads localStorage first and
 * returns whatever it finds verbatim, in any format.
 */
@Injectable({
  providedIn: 'root',
})
export class UserIdentifierService {
  // RETIRED (path 1): derived the altuid from the `_ga` cookie by hex-encoding
  // two of its fields, which made it a re-encoding of the GA client ID and so
  // analytics data by construction, minted before anyone had consented to
  // analytics. It was also the only path that did not read localStorage first:
  // it recomputed from the cookie and overwrote the stored value, so any change
  // to the `_ga` cookie silently re-keyed the visitor and orphaned their saved
  // cart. Kept here because values it produced (dash-free hex, no `t` prefix)
  // are still in circulation.
  //
  // getGoogleAnalyticsClientId(): string | null {
  //   if (this.cachedGACID !== undefined) {
  //     return this.cachedGACID;
  //   }
  //
  //   if (isPlatformBrowser(this.platformId)) {
  //     const gaCookie = this.document.cookie
  //       .split('; ')
  //       .find((row) => row.startsWith('_ga='));
  //
  //     if (gaCookie) {
  //       const cidParts = gaCookie.split('.');
  //       const part1Hex = parseInt(cidParts[2], 10).toString(16);
  //       const part2Hex = parseInt(cidParts[3], 10).toString(16);
  //       this.cachedGACID = part1Hex + part2Hex;
  //       const identifierKey = 'altuid';
  //       localStorage.setItem(identifierKey, this.cachedGACID);
  //     } else {
  //       this.cachedGACID = null;
  //     }
  //   } else {
  //     this.cachedGACID = null;
  //   }
  //
  //   return this.cachedGACID;
  // }

  /**
   * Returns the stored altuid, generating and storing one if there is none.
   * The only remaining source of an altuid.
   *
   * No longer fetches anything despite the name, which is kept only because the
   * call sites depend on it. It used to derive four of the six octets from the
   * caller's IP via a synchronous XHR to ipify; that blocked the affiliate
   * redirect in AffiliateComponent, and a synchronous XMLHttpRequest cannot be
   * given a timeout. Nothing downstream decodes the address back out of an
   * altuid, so the octets are now simply random.
   *
   * Returns null wherever localStorage is unavailable (private mode, blocked
   * storage, SSR) rather than throwing. Every caller must handle null: there is
   * no fallback generator behind this one any more.
   */
  fetchAndStoreUserIdentifierSync(): string | null {
    try {
      // Inside the try with the write: reading storage throws in the same
      // situations writing to it does, and an uncaught throw here would take out
      // the cart save and the affiliate redirect that call this.
      const storedIdentifierValue = localStorage.getItem('altuid');

      if (storedIdentifierValue) {
        return storedIdentifierValue;
      }

      const identifier = this.randomOctets(6);

      const identifierKey = 'altuid';
      localStorage.setItem(identifierKey, identifier);

      return identifier;
    } catch {
      return null;
    }
  }

  // RETIRED (path 3): hashed navigator.userAgent together with a timestamp and
  // Math.random() into a `t`-prefixed value. The user agent made it a
  // fingerprinting input rather than a random draw, for no gain over
  // crypto.getRandomValues, and its localStorage.setItem was unguarded, so it
  // threw outright wherever storage was unavailable (private mode, blocked
  // storage) instead of failing the way its only sibling does. Kept here because
  // `t`-prefixed values it produced are still in circulation.
  //
  // generateFallbackId(): string {
  //   const timestamp = new Date().getTime();
  //   const userAgent = window.navigator.userAgent;
  //   const randomFactor = Math.random().toString(36).substring(2, 15);
  //   const uniqueId = `${userAgent}-${timestamp}-${randomFactor}`;
  //
  //   let hash = 0;
  //   for (let i = 0; i < uniqueId.length; i++) {
  //     hash = (hash << 5) - hash + uniqueId.charCodeAt(i);
  //     hash |= 0;
  //   }
  //
  //   this.cachedGACID = 't' + Math.abs(hash).toString();
  //   const identifierKey = 'altuid';
  //   localStorage.setItem(identifierKey, this.cachedGACID);
  //
  //   return this.cachedGACID;
  // }

  // Dash-separated hex octets, matching the shape the IP-derived identifier had.
  // The count must stay at six: AlertComponent.parseAlertCode splits an emailed
  // alert code on '-' and only recognises a six-octet, four-octet or dash-free
  // altuid, so anything else would truncate to its first segment and break the
  // price-alert lookup.
  private randomOctets(count: number): string {
    const bytes = crypto.getRandomValues(new Uint8Array(count));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('-');
  }
}
